import React, { useState, useEffect, useCallback } from 'react';

interface PerformanceMetrics {
  pageLoadTime: number;
  apiCallCount: number;
  averageApiTime: number;
  memoryUsage: number;
  renderCount: number;
}

interface PerformanceMonitorProps {
  enabled?: boolean;
  showDetails?: boolean;
}

export const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({ 
  enabled = false, 
  showDetails = false 
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    pageLoadTime: 0,
    apiCallCount: 0,
    averageApiTime: 0,
    memoryUsage: 0,
    renderCount: 0
  });

  const [apiCalls, setApiCalls] = useState<Array<{ name: string; duration: number; timestamp: number }>>([]);
  const [isVisible, setIsVisible] = useState(false);

  // Track page load time
  useEffect(() => {
    if (!enabled) return;

    const loadTime = performance.now();
    setMetrics(prev => ({ ...prev, pageLoadTime: loadTime }));

    // Track memory usage
    const updateMemoryUsage = () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        setMetrics(prev => ({ 
          ...prev, 
          memoryUsage: memory.usedJSHeapSize / 1024 / 1024 // Convert to MB
        }));
      }
    };

    updateMemoryUsage();
    const memoryInterval = setInterval(updateMemoryUsage, 5000);

    return () => clearInterval(memoryInterval);
  }, [enabled]);

  // Track API calls
  const trackApiCall = useCallback((name: string, duration: number) => {
    if (!enabled) return;

    const newCall = { name, duration, timestamp: Date.now() };
    setApiCalls(prev => [...prev, newCall]);

    setMetrics(prev => {
      const newCount = prev.apiCallCount + 1;
      const newAverage = (prev.averageApiTime * prev.apiCallCount + duration) / newCount;
      
      return {
        ...prev,
        apiCallCount: newCount,
        averageApiTime: newAverage
      };
    });
  }, [enabled]);

  // Track render count
  useEffect(() => {
    if (!enabled) return;
    
    setMetrics(prev => ({ ...prev, renderCount: prev.renderCount + 1 }));
  });

  // Expose tracking function globally for other components
  useEffect(() => {
    if (!enabled) return;

    (window as any).trackApiCall = trackApiCall;
    
    return () => {
      delete (window as any).trackApiCall;
    };
  }, [enabled, trackApiCall]);

  // Toggle visibility with keyboard shortcut
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'P') {
        setIsVisible(prev => !prev);
      }
    };

    if (enabled) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [enabled]);

  if (!enabled || !isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 bg-black bg-opacity-90 text-white p-4 rounded-lg shadow-lg z-50 max-w-sm">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-bold">Performance Monitor</h3>
        <button 
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-white text-xs"
        >
          ✕
        </button>
      </div>

      <div className="space-y-1 text-xs">
        <div className="flex justify-between">
          <span>Page Load:</span>
          <span className={metrics.pageLoadTime > 3000 ? 'text-red-400' : 'text-green-400'}>
            {metrics.pageLoadTime.toFixed(0)}ms
          </span>
        </div>

        <div className="flex justify-between">
          <span>API Calls:</span>
          <span>{metrics.apiCallCount}</span>
        </div>

        <div className="flex justify-between">
          <span>Avg API Time:</span>
          <span className={metrics.averageApiTime > 1000 ? 'text-red-400' : 'text-green-400'}>
            {metrics.averageApiTime.toFixed(0)}ms
          </span>
        </div>

        <div className="flex justify-between">
          <span>Memory:</span>
          <span className={metrics.memoryUsage > 50 ? 'text-red-400' : 'text-green-400'}>
            {metrics.memoryUsage.toFixed(1)}MB
          </span>
        </div>

        <div className="flex justify-between">
          <span>Renders:</span>
          <span>{metrics.renderCount}</span>
        </div>
      </div>

      {showDetails && apiCalls.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-600">
          <h4 className="text-xs font-semibold mb-1">Recent API Calls:</h4>
          <div className="max-h-20 overflow-y-auto">
            {apiCalls.slice(-5).map((call, index) => (
              <div key={index} className="flex justify-between text-xs">
                <span className="truncate">{call.name}</span>
                <span className={call.duration > 1000 ? 'text-red-400' : 'text-green-400'}>
                  {call.duration.toFixed(0)}ms
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-gray-600 text-xs text-gray-400">
        Press Ctrl+Shift+P to toggle
      </div>
    </div>
  );
};

// Hook for easy performance tracking
export const usePerformanceTracking = () => {
  const trackApiCall = useCallback((name: string, duration: number) => {
    if ((window as any).trackApiCall) {
      (window as any).trackApiCall(name, duration);
    }
  }, []);

  const measureApiCall = useCallback(<T,>(
    name: string,
    apiCall: () => Promise<T>
  ): Promise<T> => {
    const startTime = performance.now();
    
    return apiCall().then(result => {
      const duration = performance.now() - startTime;
      trackApiCall(name, duration);
      return result;
    }).catch(error => {
      const duration = performance.now() - startTime;
      trackApiCall(`${name} (ERROR)`, duration);
      throw error;
    });
  }, [trackApiCall]);

  return { trackApiCall, measureApiCall };
};

// Performance boundary component
export class PerformanceBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Performance boundary caught an error:', error, errorInfo);
    
    // Track performance issues
    if ((window as any).trackApiCall) {
      (window as any).trackApiCall('Performance Error', 0);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="text-center py-8">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Something went wrong
          </h3>
          <p className="text-gray-600 mb-4">
            There was a performance issue loading this component.
          </p>
          <button 
            onClick={() => this.setState({ hasError: false })}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
} 